/* eslint-disable max-len */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Grid, Input, Button, withStyles } from 'material-ui';
import Table, { TableBody, TableCell, TableRow } from 'material-ui/Table';
import { Field, Form } from 'redux-form';
import { formatCurrency, paymentTypes, TextInput } from 'entoro-shared';

const styles = () => ({
  horizontalLine: {
    height: '1px',
    backgroundColor: '#ebebeb',
    marginBottom: '10px',
    marginTop: '40px',
  },
  title: {
    fontSize: '14px',
    marginBottom: '10px',
    marginTop: '20px',
  },
  subheading: {
    color: '#8c8c8c',
    fontSize: '14px',
    fontWeight: '300',
    marginBottom: '10px',
  },
  button: {
    backgroundColor: '#3a61ad',
    color: 'white',
    fontSize: '14px',
    height: '100%',
    textTransform: 'capitalize',
    width: '100%',
    '&:hover': {
      background: '#3a61ad',
    },
  },
  buttonSuccess: {
    backgroundColor: '#417506',
    color: 'white',
    fontSize: '14px',
    height: '100%',
    textTransform: 'capitalize',
    width: '100%',
    '&:hover': {
      background: '#417506',
    },
  },
  input: {
    height: '100%',
    padding: '0',
    pointerEvents: 'none',
  },
  textField: {
    backgroundColor: '#cecece',
    border: 'none',
    borderRadius: '2px',
    height: '40px',
    padding: '2px 8px',
    '&:before': {
      content: 'none',
    },
    '&:after': {
      content: 'none',
    },
  },
});

class PaymentDetails extends Component {
  constructor(props) {
    super(props);

    this.state = {
      copied: false,
    };

    this.inputRef = null;

    this.setInputRef = (ref) => {
      this.inputRef = ref;
    };

    this.copyToClipboard = (e) => {
      if (this.inputRef) {
        this.inputRef.select();
        document.execCommand('copy');
        e.target.focus();
        this.setState({ copied: true });
      }
    };
  }

  render() {
    const {
      tenant: { primaryContactEmail },
    } = this.context;

    const {
      offer,
      selectedPaymentMethod,
      selectedPaymentType,
      initialValues,
      paymentDetails,
      handleSubmit,
      classes,
    } = this.props;

    const {
      investmentAmount,
      investmentAmountCrypto,
      paymentMethod,
      paymentType,
    } = initialValues;

    const {
      bankName,
      bankAddress,
      beneficiaryName,
      swiftCode,
      accountNumber,
      routingNumber,
      memoLine,
    } = paymentDetails;

    const { copied } = this.state;

    const cryptoType = selectedPaymentType.replace('CRYPTO_', '').toLowerCase();
    const cryptoWallet = `${cryptoType}WalletAddress`;
    const cryptoWalletAddress = offer.paymentDetails[cryptoWallet];

    return (
      <div className='step'>
        <Grid container spacing={16} direction='row'>
          <Grid item xs={12} sm={6}>
            <span className={classes.title}>
              Payment Amount: &nbsp;
              <b>{formatCurrency(investmentAmount)} USD</b>
            </span>
          </Grid>
          <Grid item xs={12} sm={6}>
            <span className={classes.title}>
              Payment Method:&nbsp;
              <b>{paymentTypes.getLabel(paymentType)}</b>
            </span>
          </Grid>
          {paymentMethod === 'CRYPTO' ? (
            <Grid item xs={12}>
              <span className={classes.crypto}>
                <i>
                  {investmentAmountCrypto} {paymentTypes.getLabel(paymentType)}
                </i>
              </span>
            </Grid>
          ) : null}
        </Grid>
        {selectedPaymentMethod === 'CRYPTO' && (
          <Form onSubmit={handleSubmit}>
            <Grid container spacing={16} direction='row'>
              <Grid item xs={12}>
                <p className={classes.title}>
                  Enter your Ethereum Wallet Address*
                </p>
                <p className={classes.subheading}>
                  Your Digital Securities will be sent to this wallet, please
                  make sure you validate it. Do not use an Exchange Address
                  &#40;if you do, the tokens will be sent to the exchange, not
                  to you&#41;. Your wallet must be on the Ethereum network. If
                  you need assistance setting up a wallet to receive your
                  Digital Securities please contact{' '}
                  <a href={`mailto:${primaryContactEmail}`}>
                    {primaryContactEmail}
                  </a>
                  .
                </p>
                <Field component={TextInput} name='walletAddress' />
              </Grid>
              <Grid item xs={12}>
                <p>
                  Send funds to this {paymentTypes.getLabel(paymentType)} Wallet
                  Address
                </p>
                <Grid container spacing={16} direction='row'>
                  <Grid item xs={12} sm={9}>
                    <Input
                      inputRef={this.setInputRef}
                      disableUnderline
                      classes={{ input: classes.input }}
                      className={classes.textField}
                      fullWidth
                      value={cryptoWalletAddress}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      onClick={this.copyToClipboard}
                      className={
                        copied ? classes.buttonSuccess : classes.button
                      }
                    >
                      {copied ? 'Copied!' : 'Copy To Clipboard'}
                    </Button>
                  </Grid>
                </Grid>
                <div className={classes.horizontalLine} />
                <span className={classes.subheading}>
                  * You may enter your wallet address at a later time
                </span>
              </Grid>
            </Grid>
          </Form>
        )}
        <br />
        {selectedPaymentMethod === 'BANK' && (
          <Grid container spacing={16} direction='row'>
            <Grid item xs={12}>
              <p>Submit your payment</p>
              <p className={classes.subheading}>
                Initiate your payment to Entoro Securities with the information
                provided below.
              </p>
            </Grid>
            <Grid item xs={12}>
              <Table>
                <TableBody>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.labelCell}>Bank:</TableCell>
                    <TableCell className={classes.cell}>{bankName}</TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.labelCell}>
                      Bank Address:
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {bankAddress}
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.labelCell}>
                      Beneficiary Name:
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {beneficiaryName}
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.labelCell}>
                      Swift Code:
                    </TableCell>
                    <TableCell className={classes.cell}>{swiftCode}</TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.labelCell}>
                      Escrow (Master) Account #
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {accountNumber}
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.labelCell}>
                      ABA/Routing Number
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {routingNumber}
                    </TableCell>
                  </TableRow>
                  <TableRow className={classes.row}>
                    <TableCell className={classes.labelCell}>
                      Memo Line:
                    </TableCell>
                    <TableCell className={classes.cell}>{memoLine}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        )}
      </div>
    );
  }
}

PaymentDetails.propTypes = {
  classes: PropTypes.object.isRequired,
  offer: PropTypes.object.isRequired,
  selectedPaymentMethod: PropTypes.string.isRequired,
  selectedPaymentType: PropTypes.string.isRequired,
  paymentDetails: PropTypes.object.isRequired,
  initialValues: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
};

PaymentDetails.contextTypes = {
  tenant: PropTypes.object,
};

export default withStyles(styles)(PaymentDetails);



// WEBPACK FOOTER //
// ./src/components/OfferOverview/ReadyToInvestButton/PaymentProcess/PaymentProcessForm/PaymentDetails.js